import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormEnumMultiselect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumMultiselect";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { IntakeTabBar } from "@/components/companies/shared/IntakeTabBar";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useGetJobStakeholders } from "@/hooks/queries/useGetJobStakeholders";
import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import {
  EMPTY_PROGRESS,
  interviewRoundTypeOptions,
  interviewRoundTypeToLabel,
} from "@/lib/constants";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  InterviewRound,
  InterviewRoundType,
  JobIntake,
  JobIntakeLaborupRound,
  Progress,
  Stakeholder,
} from "@/types";

const laborupRoundSchema = z.object({
  submissionProcess: z.string().nullable(),
  additionalExpectations: z.string().nullable(),
});

const roundSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  type: z.nativeEnum(InterviewRoundType).nullable(),
  conductedBy: z.array(z.number()),
  shieldedHiringManager: z.string().nullable(),
  details: z.string().nullable(),
  commonQuestions: z.string().nullable(),
  passingInterviewDetails: z.string().nullable(),
  failingInterviewDetails: z.string().nullable(),
});

const emptyRound = (): InterviewRound => ({
  id: crypto.randomUUID(),
  title: null,
  type: null,
  conductedBy: [],
  shieldedHiringManager: null,
  details: null,
  commonQuestions: null,
  passingInterviewDetails: null,
  failingInterviewDetails: null,
});

export const InterviewProcess = ({
  isEditing,
  jobIntake,
  setIsEditing,
  laborupRoundProgress,
  roundsProgress,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
  setIsEditing: (isEditing: boolean) => void;
  laborupRoundProgress: Progress;
  roundsProgress: Record<string, Progress>;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: jobStakeholders } = useGetJobStakeholders();

  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  /* React Hooks */
  // useState
  const [rounds, setRounds] = useState<InterviewRound[]>(
    jobIntake.interviewProcess,
  );
  // -1 means no round is selected - defaults the user to see the Laborup round
  const [selectedIndex, setSelectedIndex] = useState(-1);

  /* Handlers */
  const saveAll = (updated: InterviewRound[]) => {
    updateJobIntake({
      updateJobIntakeRequest: { interviewProcess: updated },
    });
  };

  const handleAdd = () => {
    const updated = [...rounds, emptyRound()];
    setRounds(updated);
    setSelectedIndex(updated.length - 1);
    setIsEditing(true);
    saveAll(updated);
  };

  const handleDelete = (id: string) => {
    const deletedIndex = rounds.findIndex((r) => r.id === id);
    const updated = rounds.filter((r) => r.id !== id);
    setRounds(updated);
    const newIndex =
      deletedIndex < selectedIndex ? selectedIndex - 1 : selectedIndex;
    setSelectedIndex(Math.min(Math.max(0, newIndex), updated.length - 1));
    saveAll(updated);
  };

  return (
    <Column className="gap-4">
      {/* top offset = combined height of IntakeHeader */}
      <Column className="sticky top-[8.78rem] z-10 bg-white">
        <IntakeTabBar
          addLabel="Add Round"
          deleteDescription="This will permanently delete this interview round."
          onAdd={handleAdd}
          onDelete={() => {
            const selected = rounds[selectedIndex];
            handleDelete(selected.id);
          }}
          screenName={MIXPANEL_SCREENS.JOB}
          removeButtonProps={{
            disabled: selectedIndex === -1,
            message: "Expectations for Laborup cannot be deleted.",
          }}
        >
          <IntakeTab
            key="laborup-round"
            isSelected={selectedIndex === -1}
            label="Expectations for Laborup"
            onSelect={() => {
              setSelectedIndex(-1);
            }}
            progress={laborupRoundProgress}
          />
          {rounds.map((round, index) => (
            <IntakeTab
              key={round.id}
              isSelected={selectedIndex === index}
              label={round.title ?? `Round ${String(index + 1)}`}
              onSelect={() => {
                setSelectedIndex(index);
              }}
              progress={roundsProgress[round.id] ?? EMPTY_PROGRESS}
            />
          ))}
        </IntakeTabBar>
      </Column>
      <InterviewSectionContent
        isEditing={isEditing}
        jobStakeholders={jobStakeholders ?? []}
        laborupRound={jobIntake.laborupRound}
        rounds={rounds}
        saveAll={saveAll}
        selectedIndex={selectedIndex}
        setRounds={setRounds}
      />
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const InterviewSectionContent = ({
  isEditing,
  jobStakeholders,
  laborupRound,
  rounds,
  saveAll,
  selectedIndex,
  setRounds,
}: {
  isEditing: boolean;
  jobStakeholders: Stakeholder[];
  laborupRound: JobIntakeLaborupRound;
  rounds: InterviewRound[];
  saveAll: (updated: InterviewRound[]) => void;
  selectedIndex: number;
  setRounds: (updated: InterviewRound[]) => void;
}) => {
  const handleUpdate = (data: InterviewRound) => {
    const updated = rounds.map((r) => (r.id === data.id ? data : r));
    setRounds(updated);
    saveAll(updated);
  };

  // Edit Mode
  if (isEditing) {
    // Edit Mode for a specific round
    if (selectedIndex !== -1) {
      const round = rounds[selectedIndex] ?? emptyRound();
      return (
        <RoundForm
          key={round.id}
          onSave={handleUpdate}
          round={round}
          stakeholders={jobStakeholders}
        />
      );
    }
    // Edit Mode for the Laborup round
    else {
      return <LaborupRoundForm laborupRound={laborupRound} />;
    }
  }
  // Read Only Mode
  else {
    // Read Only Mode for a specific round
    if (selectedIndex !== -1) {
      const round = rounds[selectedIndex] ?? emptyRound();
      return (
        <RoundReadOnly
          key={round.id}
          round={round}
          stakeholders={jobStakeholders}
        />
      );
    }
    // Read Only Mode for the Laborup round
    else {
      return <LaborupRoundReadOnly laborupRound={laborupRound} />;
    }
  }
};

const RoundForm = ({
  onSave,
  round,
  stakeholders,
}: {
  onSave: (data: InterviewRound) => void;
  round: InterviewRound;
  stakeholders: Stakeholder[];
}) => {
  const form = useForm<z.infer<typeof roundSchema>>({
    resolver: zodResolver(roundSchema),
    defaultValues: round,
  });

  const stakeholderIdToLabel = Object.fromEntries(
    stakeholders.map((s) => [
      s.id,
      `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "-",
    ]),
  ) as Record<number, string>;

  const stakeholderOptions = stakeholders.map((s) => ({
    value: s.id,
    label: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "-",
  }));

  const saveOnBlur = () => {
    void form.handleSubmit((data) => {
      onSave(data);
    })();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-6">
        <AutosaveFormTextInput
          fieldName="title"
          form={form}
          label="Title"
          onBlur={saveOnBlur}
          placeholder="e.g., Phone Screen, On-Site Interview"
        />
        <Row className="gap-4">
          <AutosaveFormEnumSelect
            className="flex-1"
            fieldName="type"
            form={form}
            label="Type"
            onSelect={saveOnBlur}
            options={interviewRoundTypeOptions}
          />
          <AutosaveFormEnumMultiselect
            className="flex-1"
            enumToLabel={stakeholderIdToLabel}
            fieldName="conductedBy"
            form={form}
            label="Conducted By"
            onSelect={saveOnBlur}
            options={stakeholderOptions}
          />
        </Row>
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="shieldedHiringManager"
          form={form}
          infoTooltip={`Is there hiring manager that Laborup has been shielded from for this interview round?\nInclude all information we know about them.`}
          label="Hiring Manager Laborup has been shielded from"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="details"
          form={form}
          infoTooltip={`What does this interview round look like — format, length, setting?\nWhat are they evaluating or testing for in this round?`}
          label="Details"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="commonQuestions"
          form={form}
          infoTooltip={`Are there specific questions the interviewer tends to ask in this round?\nWhat are they trying to learn from those questions and what does a good answer look like?`}
          label="Common Questions"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="passingInterviewDetails"
          form={form}
          infoTooltip={`When someone passes this round, what did they do well?\nWhat traits or answers stood out to the interviewer?`}
          label="Passing Interview Details"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="failingInterviewDetails"
          form={form}
          infoTooltip={`What typically causes candidates to fail this round?\nAre there common mistakes or red flags the interviewer watches for?`}
          label="Failing Interview Details"
          onBlur={saveOnBlur}
        />
      </Column>
    </IntakeForm>
  );
};

const LaborupRoundForm = ({
  laborupRound,
}: {
  laborupRound: JobIntakeLaborupRound;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  const form = useForm<z.infer<typeof laborupRoundSchema>>({
    resolver: zodResolver(laborupRoundSchema),
    defaultValues: laborupRound,
  });

  /* Handlers */
  const handleSave = (data: z.infer<typeof laborupRoundSchema>) => {
    updateJobIntake({
      updateJobIntakeRequest: {
        laborupRound: data,
      },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-6">
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="submissionProcess"
          form={form}
          infoTooltip={`What is the process for submitting candidates to this client?\nDoes the client want batches sent? If so what size? How often should/could they be submitted?\nDo they prefer time just put on their calendar? Which stakeholder's calendar or link should be used? Are there certain times of day they prefer to conduct interviews?`}
          label="Submission Process"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="additionalExpectations"
          form={form}
          infoTooltip={`Are there additional expectations that this client has for Laborup?\nAre there specific communication updates they expect to receive?`}
          label="Additional Expectations"
          onBlur={saveOnBlur}
        />
      </Column>

      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </IntakeForm>
  );
};

const RoundReadOnly = ({
  round,
  stakeholders,
}: {
  round: InterviewRound;
  stakeholders: Stakeholder[];
}) => {
  const typeLabel = round.type ? interviewRoundTypeToLabel[round.type] : null;
  const conductedByLabel =
    round.conductedBy.length > 0
      ? round.conductedBy
          .map((id) => {
            const s = stakeholders.find((st) => st.id === id);
            return s
              ? `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "-"
              : String(id);
          })
          .join(", ")
      : null;

  return (
    <Column className="gap-4">
      <Row className="gap-4">
        <ReadOnlyField
          className="flex-1"
          label="Title"
          value={round.title ?? null}
        />
        <ReadOnlyField className="flex-1" label="Type" value={typeLabel} />
        <ReadOnlyField
          className="flex-1"
          label="Conducted By"
          value={conductedByLabel}
        />
      </Row>
      <ReadOnlyRichTextField
        label="Hiring Manager Laborup has been shielded from"
        value={round.shieldedHiringManager ?? null}
      />
      <ReadOnlyRichTextField label="Details" value={round.details ?? null} />
      <ReadOnlyRichTextField
        label="Common Questions"
        value={round.commonQuestions ?? null}
      />
      <ReadOnlyRichTextField
        label="Passing Interview Details"
        value={round.passingInterviewDetails ?? null}
      />
      <ReadOnlyRichTextField
        label="Failing Interview Details"
        value={round.failingInterviewDetails ?? null}
      />
    </Column>
  );
};

const LaborupRoundReadOnly = ({
  laborupRound,
}: {
  laborupRound: JobIntakeLaborupRound;
}) => {
  return (
    <Column className="gap-4">
      <ReadOnlyRichTextField
        label="Submission Process"
        value={laborupRound.submissionProcess ?? null}
      />
      <ReadOnlyRichTextField
        label="Additional Expectations"
        value={laborupRound.additionalExpectations ?? null}
      />
    </Column>
  );
};
